import React, { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "ncoded-component-library";
import api from "api";
import { UserBlockEntity } from "models/Moderation";
import SimplePagination from "./SimplePagination";

const PAGE_SIZE = 10;
const QUERY_KEY = "admin-user-blocks";

const formatUser = (u?: { id: number; firstName?: string; lastName?: string }) =>
  !u ? "-" : `#${u.id} ${u.firstName || ""} ${u.lastName || ""}`.trim();

const UserBlocksTable = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const queryClient = useQueryClient();

  const fetchBlocks = useCallback(async () => {
    const { data } = await api.moderation.getUserBlocks({
      $take: PAGE_SIZE,
      $page: pageIndex + 1,
      $order: "-id",
      $relations: "blocker,blocked",
    });
    return data;
  }, [pageIndex]);

  const query = useQuery({
    queryKey: [QUERY_KEY, pageIndex],
    queryFn: fetchBlocks,
    keepPreviousData: true,
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => api.moderation.removeUserBlock(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  const rows: UserBlockEntity[] = query.data?.items || [];
  const pageCount = query.data?.totalPages || 0;

  return (
    <div>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Blocker</th>
            <th>Blocked</th>
            <th>Reason</th>
            <th>Info</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !query.isFetching ? (
            <tr>
              <td colSpan={7}>No blocks.</td>
            </tr>
          ) : null}
          {rows.map((b) => (
            <tr key={b.id}>
              <td>{b.id}</td>
              <td>{formatUser(b.blocker)}</td>
              <td>{formatUser(b.blocked)}</td>
              <td>{b.reason || "-"}</td>
              <td>{b.info || "-"}</td>
              <td>{new Date(b.createdAt).toLocaleString()}</td>
              <td>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Remove this block? The user will be visible again to the blocker."
                      )
                    ) {
                      removeMutation.mutate(b.id);
                    }
                  }}
                  disabled={removeMutation.isLoading}
                >
                  Remove
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SimplePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        onPrev={() => setPageIndex((i) => Math.max(0, i - 1))}
        onNext={() => setPageIndex((i) => i + 1)}
        isFetching={query.isFetching}
      />
    </div>
  );
};

export default UserBlocksTable;
